import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/api";
import {
  Users, AlertTriangle, Clock3, CheckCircle2, TrendingUp, Building2, Calculator,
} from "lucide-react";

const TEAM_BANNER =
  "https://images.unsplash.com/photo-1758518730151-cf64fddb4f0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function Stat({ icon: Icon, label, value, accent, sub, testid }) {
  return (
    <div data-testid={testid} className="card-elegant p-6 fade-up">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[var(--text-muted)] text-xs uppercase tracking-wider font-semibold">{label}</div>
          <div className="font-mono text-3xl font-semibold mt-2" style={{ color: accent }}>{value}</div>
          {sub && <div className="text-xs text-[var(--text-secondary)] mt-1">{sub}</div>}
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}18` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    const t = setInterval(() => api.get("/dashboard").then((r) => setD(r.data)), 20000);
    return () => clearInterval(t);
  }, []);

  if (!d) return <div className="text-[var(--text-muted)]">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <div className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] font-semibold">Executive Overview</div>
        <h1 className="font-serif text-4xl mt-1">Good day, {user?.name?.split(" ")[0]}.</h1>
        <p className="text-[var(--text-secondary)] mt-1">Team performance, targets & KPIs at a glance.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <Stat testid="stat-clients" icon={Building2} label="Active Clients" value={d.total_clients} accent="#C5A059" sub="Onboarded & active" />
        <Stat testid="stat-urgent" icon={AlertTriangle} label="Urgent Tasks" value={d.urgent_tasks} accent="#DC2626" sub={`${d.pending_tasks} pending`} />
        <Stat testid="stat-attendance" icon={Clock3} label="Present Today" value={`${d.attendance_today}/${d.total_staff}`} accent="#0D9488" sub={`WFA ${d.wfa} · WFO ${d.wfo}`} />
        <Stat testid="stat-target" icon={TrendingUp} label="Team Target" value={`${d.kpi_completion}%`} accent="#141518" sub={`${d.complete_tasks}/${d.total_tasks} tasks done`} />
      </div>

      <div className="card-elegant p-7 fade-up relative overflow-hidden" style={{ background: "linear-gradient(120deg,#111318,#20242d)" }}>
        <div className="relative z-10">
          <div className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] font-semibold">Daily Motivation</div>
          <div className="font-serif text-2xl text-white mt-2 max-w-2xl">
            {d.kpi_completion >= 80 ? "Outstanding momentum — the team is crushing its targets. Keep the standard high! 🏆" :
             d.kpi_completion >= 50 ? "Solid progress. A focused push on the urgent items gets us to the finish line. 💪" :
             "Every completed task moves a client forward. Let's turn today's pending into wins. 🚀"}
          </div>
          <div className="text-white/60 text-sm mt-3">
            {d.urgent_tasks > 0 ? `${d.urgent_tasks} urgent task(s) need attention today.` : "No urgent tasks right now — great work staying ahead."}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* KPI gauge */}
        <div className="card-elegant p-7 lg:col-span-1 fade-up">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[var(--gold)]" />
            <h3 className="font-serif text-xl">Team KPI Target</h3>
          </div>
          <div className="flex flex-col items-center py-4">
            <div className="relative w-40 h-40">
              <svg className="w-full h-full -rotate-90">
                <circle cx="80" cy="80" r="66" fill="none" stroke="#F0EADB" strokeWidth="14" />
                <circle
                  cx="80" cy="80" r="66" fill="none" stroke="#C5A059" strokeWidth="14" strokeLinecap="round"
                  strokeDasharray={`${(d.kpi_completion / 100) * 415} 415`}
                  style={{ transition: "stroke-dasharray 1s ease" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-mono text-3xl font-bold text-[var(--text-primary)]">{d.kpi_completion}%</div>
                <div className="text-xs text-[var(--text-muted)]">Completion</div>
              </div>
            </div>
            <div className="text-sm text-[var(--text-secondary)] mt-4 text-center">
              {d.complete_tasks} of {d.total_tasks} scheduled tasks completed this cycle.
            </div>
          </div>
        </div>

        {/* Task breakdown */}
        <div className="card-elegant p-7 lg:col-span-2 fade-up">
          <h3 className="font-serif text-xl mb-5">Project Task Distribution</h3>
          <div className="space-y-4">
            {[
              { label: "Urgent", value: d.urgent_tasks, color: "#DC2626" },
              { label: "Pending", value: d.pending_tasks, color: "#D97706" },
              { label: "Complete (Archived)", value: d.complete_tasks, color: "#16A34A" },
            ].map((row) => {
              const total = d.total_tasks || 1;
              return (
                <div key={row.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[var(--text-secondary)]">{row.label}</span>
                    <span className="font-mono font-semibold">{row.value}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[var(--bg-subtle)] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(row.value / total) * 100}%`, background: row.color, transition: "width 0.8s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 rounded-xl overflow-hidden relative h-28">
            <img src={TEAM_BANNER} alt="team" className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center px-6" style={{ background: "linear-gradient(90deg, rgba(17,19,24,0.85), rgba(17,19,24,0.2))" }}>
              <div className="text-white">
                <div className="font-serif text-lg">Excellence, delivered together.</div>
                <div className="text-white/60 text-xs">PT. Savior Prime Indonesia — Finance Consulting Team</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
